"use client";

import Link from "next/link";
import {
  ServicePageLayout,
  ServiceHeroImage,
  ServiceSection,
  ServiceGrid,
  ServiceBenefitItem,
  ServiceFaq,
  ConsultationCTA,
  type ServiceFaqItem,
} from "@/components/services";
import { CTAButton } from "@/components/ui/cta-button";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { cn } from "@/lib/utils";

// Breaks a band section out of the layout's centered container to full viewport width.
const FULL_BLEED = "mx-[calc(50%_-_50vw)]";

// Service image from public directory
const hspImage = "/images/services/svc-hardware-software.jpg";

// Authorized partner brands shown in the trust strip beneath the hero image.
const partnerBrands = [
  { name: "Lenovo", logo: "/images/partners/lenovo.png" },
  { name: "Microsoft", logo: "/images/partners/microsoft.png" },
  { name: "Adobe", logo: "/images/partners/adobe.png" },
];

// Key stats merged from the old TrustBar into the trust strip.
const trustStats = [
  { value: "1000+", label: "Devices Deployed" },
  { value: "48hr", label: "Fast Deployment" },
  { value: "100%", label: "Genuine Products" },
  { value: "24/7", label: "Support Available" },
];

interface LenovoProduct {
  name: string;
  description: string;
  features: string[];
  popular?: boolean;
}

const lenovoProducts: LenovoProduct[] = [
  {
    name: "ThinkPad X1 Carbon",
    description: "Ultra-light business laptop with military-grade durability. Perfect for executives and mobile professionals.",
    features: ["14\" 2.8K OLED Display", "Intel Core Ultra 7", "Up to 32GB RAM", "All-day battery life"],
    popular: true,
  },
  {
    name: "ThinkPad T Series",
    description: "Reliable workhorse for enterprise deployment. Balances performance, security, and value.",
    features: ["14\" or 15.6\" FHD Display", "Intel Core i5/i7", "MIL-STD-810H certified", "ThinkShield security"],
  },
  {
    name: "ThinkPad E Series",
    description: "Entry-level business laptop with essential features. Ideal for growing businesses.",
    features: ["15.6\" FHD Display", "Intel Core i5", "Cost-effective", "Proven ThinkPad reliability"],
  },
  {
    name: "ThinkCentre Desktops",
    description: "Space-saving, powerful desktops for office environments. Available in tower, small form factor, and tiny.",
    features: ["Multiple form factors", "Intel Core processors", "Enterprise manageability", "Modular design"],
  },
];

// Full catalog rendered as compact hairline rows — one row per category.
interface ProductCategory {
  name: string;
  description: string;
  products: string[];
  href: string;
  linkLabel: string;
  featured?: boolean;
}

const productCategories: ProductCategory[] = [
  {
    name: "Microsoft 365 & Azure",
    description: "Productivity suites, cloud services, and Windows licensing for businesses of all sizes.",
    products: ["Microsoft 365 Business", "Azure Cloud Services", "Windows 11 Pro", "Microsoft Teams"],
    href: "/contact/",
    linkLabel: "Get Quote",
  },
  {
    name: "Adobe Creative Cloud",
    description: "Creative Cloud licenses, Acrobat Pro, and enterprise agreements for creative teams.",
    products: ["Creative Cloud for Teams", "Acrobat Pro", "Photoshop", "Premiere Pro"],
    href: "/enterprise/adobe-malaysia/",
    linkLabel: "View Adobe Products",
    featured: true,
  },
  {
    name: "AutoCount Accounting",
    description: "Malaysia's leading accounting software with LHDN e-Invoice compliance built-in.",
    products: ["AutoCount Accounting", "AutoCount POS", "E-Invoice Module", "Cloud Payroll"],
    href: "/contact/",
    linkLabel: "Get Quote",
  },
  {
    name: "ClearTax E-Invoice",
    description: "MDEC-accredited e-invoicing platform with seamless ERP integration and LHDN compliance.",
    products: ["E-Invoice Platform", "ERP Integration", "Multi-Channel Support", "Buyer Portal"],
    href: "/contact/",
    linkLabel: "Get Quote",
  },
  {
    name: "Smart CCTV & Security",
    description: "AI-powered surveillance systems with face recognition, analytics, and remote monitoring.",
    products: ["IP Camera Systems", "AI Video Analytics", "Access Control", "Remote Monitoring"],
    href: "/contact/",
    linkLabel: "Get Quote",
  },
  {
    name: "Hai Robotics Automation",
    description: "Autonomous warehouse robots that increase storage density by 80-400% and picking efficiency by 3-4x.",
    products: ["HaiPick ACR Systems", "Warehouse Automation", "HAI Q Software", "Custom Integration"],
    href: "/contact/",
    linkLabel: "Get Quote",
  },
];

const whyChooseUsBenefits = [
  {
    title: "Authorized Partner",
    description: "Official partnerships with Lenovo, Microsoft, Adobe and more. 100% genuine products with full manufacturer warranty.",
  },
  {
    title: "Volume Pricing",
    description: "Enterprise and volume licensing discounts. Flexible payment terms and leasing options for large deployments.",
  },
  {
    title: "Local Expert Support",
    description: "Malaysian-based technical team for deployment, troubleshooting, and ongoing support. No overseas call centers.",
  },
  {
    title: "Fast Deployment",
    description: "Quick license provisioning and hardware delivery. Get your team productive in days, not weeks.",
  },
  {
    title: "Asset Management",
    description: "Simplified license tracking, renewal reminders, and compliance reporting. We help you manage your entire IT inventory.",
  },
  {
    title: "Training & Onboarding",
    description: "Optional training sessions for software and systems. Help your team maximize productivity from day one.",
  },
];

// Sectors served — condensed from the old icon-tile section into a chip row.
const industriesServed = [
  "Manufacturing",
  "Retail & E-commerce",
  "Professional Services",
  "Education",
  "Healthcare",
  "Logistics & Warehouse",
  "Financial Services",
  "Government & GLC",
];

const hspFaqs: ServiceFaqItem[] = [
  {
    question: "Are all products genuine with manufacturer warranty?",
    answer: "Yes, we are authorized partners for all brands we carry including Lenovo, Microsoft and Adobe. Every product comes with full manufacturer warranty and support. We never sell grey market or refurbished products unless explicitly requested."
  },
  {
    question: "What volume discounts are available?",
    answer: "Volume pricing varies by product and quantity. Generally, orders of 10+ units qualify for tiered discounts. For software licensing, we offer VIP/Open Value programs with significant savings for multi-year commitments. Contact us for a customized quote."
  },
  {
    question: "How fast can you deliver hardware?",
    answer: "Stock items typically ship within 24-48 hours. For custom-configured laptops and servers, lead time is usually 5-10 business days. Software licenses can be provisioned same-day in most cases. We also offer express delivery for urgent requirements."
  },
  {
    question: "What support do you provide after purchase?",
    answer: "We provide deployment assistance, configuration support, and ongoing technical help. For hardware, we coordinate warranty claims directly with manufacturers. For software, we assist with license management, renewals, and user provisioning. Our support team is based in Malaysia for responsive service."
  },
  {
    question: "Do you support LHDN e-Invoice requirements?",
    answer: "Yes, we're fully compliant with Malaysia's e-Invoice mandate. Both AutoCount and ClearTax solutions we offer are LHDN-certified. We can help you select the right solution based on your existing systems and integration requirements."
  },
  {
    question: "Do you offer leasing or financing options?",
    answer: "Yes, we partner with financial institutions to offer device leasing and financing programs. This is particularly popular for large laptop deployments. Terms typically range from 12-36 months with options for technology refresh at end of term."
  }
];

export default function HardwareSoftware() {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);

  return (
    <ServicePageLayout
      title="Enterprise Hardware & Software Solutions"
      breadcrumb="Services / Hardware & Software"
      currentServiceId="hsp"
      accent="Hardware & Software"
      dek="Genuine licenses, enterprise-grade hardware, and local expert support. From Lenovo ThinkPad to Microsoft 365 — we equip Malaysian businesses for success."
      ctaLabel="Get Enterprise Quote"
    >
      <ServiceHeroImage
        src={hspImage}
        alt={translate("Enterprise hardware and software deployment")}
      />

      {/* Trust strip — authorized-partner badge, brand logos, and key stats merged */}
      <div className="mb-4 overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 border-b border-border px-6 py-4">
          <p className="flex items-center gap-2.5 text-sm font-semibold tracking-wide text-neutral-emphasis">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" aria-hidden />
            {translate("Authorized IT Partner Malaysia")}
          </p>
          <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
            {partnerBrands.map((brand) => (
              <img
                key={brand.name}
                src={brand.logo}
                alt={brand.name}
                className="h-6 w-auto object-contain"
              />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-y-5 px-6 py-5 sm:grid-cols-4">
          {trustStats.map((stat) => (
            <div key={stat.label}>
              <p className="font-display text-2xl font-bold leading-tight text-neutral-emphasis">
                {stat.value}
              </p>
              <p className="text-xs text-neutral-muted sm:text-sm">
                {translate(stat.label)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <ServiceSection
        id="products"
        title="Lenovo Business Solutions"
        eyebrow="Featured Partner"
        accent="Lenovo"
        dek="From ThinkPad laptops to ThinkCentre desktops, equip your team with industry-leading business devices backed by legendary reliability."
        band="ivory"
        className={FULL_BLEED}
      >
        <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-border bg-card px-5 py-2.5">
          <img
            src="/images/partners/lenovo.png"
            alt="Lenovo"
            className="h-7 w-auto rounded-md bg-white p-1"
          />
          <span className="text-sm font-semibold text-neutral-emphasis">
            {translate("Authorized Lenovo Partner")}
          </span>
          <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <ServiceGrid columns={2} gap="medium">
          {lenovoProducts.map((product) => (
            <div
              key={product.name}
              className={cn(
                "relative flex h-full flex-col rounded-2xl border bg-card p-6",
                product.popular ? "border-primary/40" : "border-border",
              )}
            >
              {product.popular ? (
                <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                  {translate("Best Seller")}
                </span>
              ) : null}
              <h3 className="mb-2 font-display text-xl font-bold text-neutral-emphasis">
                {translate(product.name)}
              </h3>
              <p className="mb-4 text-sm leading-relaxed text-neutral-muted">
                {translate(product.description)}
              </p>
              <ul className="mt-auto space-y-1.5">
                {product.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm text-neutral-muted">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" aria-hidden />
                    {translate(feature)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </ServiceGrid>

        {/* Volume deployment CTA — single quote action for the whole section */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                {translate("Deploying 10+ Devices?")}
              </h3>
              <p className="mt-1 max-w-xl text-neutral-muted">
                {translate("Get special volume pricing, extended warranty options, and priority deployment support for your fleet.")}
              </p>
            </div>
            <CTAButton
              href="/contact/"
              size="large"
              className="w-full shrink-0 justify-center whitespace-nowrap rounded-full font-semibold transition-colors hover:bg-primary-emphasis sm:w-auto"
            >
              Get Volume Quote
            </CTAButton>
          </div>
        </div>
      </ServiceSection>

      <ServiceSection
        title="Complete IT Solutions Catalog"
        eyebrow="Product Categories"
        accent="Catalog"
        dek="From productivity software to warehouse automation — we source and support the technology your business needs."
        band="surface"
        className={FULL_BLEED}
      >
        <div className="divide-y divide-border border-y border-border">
          {productCategories.map((category) => (
            <div
              key={category.name}
              className="grid gap-3 py-6 sm:grid-cols-[240px_minmax(0,1fr)] sm:gap-10"
            >
              <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                {translate(category.name)}
              </h3>
              <div>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {translate(category.description)}
                </p>
                <p className="mt-2 font-mono text-xs text-neutral-muted">
                  {category.products.map((product) => translate(product)).join(" · ")}
                </p>
                <Link
                  href={getLocalizedPathname(category.href, locale)}
                  className={cn(
                    "mt-3 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors",
                    category.featured
                      ? "text-primary hover:text-primary-emphasis"
                      : "text-neutral-emphasis hover:text-primary",
                  )}
                >
                  {translate(category.linkLabel)}
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </ServiceSection>

      <ServiceSection
        title="Your Trusted IT Partner in Malaysia"
        eyebrow="Why Choose Us"
        accent="Trusted IT Partner"
        dek="More than just a vendor — we provide end-to-end support to ensure your technology investment delivers maximum value."
        band="ivory"
        className={FULL_BLEED}
      >
        <ServiceGrid columns={2} gap="medium">
          {whyChooseUsBenefits.map((benefit) => (
            <ServiceBenefitItem
              key={benefit.title}
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </ServiceGrid>

        {/* Industries served — condensed to a chip row */}
        <div className="mt-12">
          <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-neutral-muted">
            {translate("Industries we serve")}
          </p>
          <div className="flex flex-wrap gap-2">
            {industriesServed.map((industry) => (
              <span
                key={industry}
                className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-neutral-muted"
              >
                {translate(industry)}
              </span>
            ))}
          </div>
        </div>
      </ServiceSection>

      {/* FAQ Section — JSON-LD for these stays co-located in page.tsx */}
      <ServiceSection
        title="Frequently Asked Questions"
        eyebrow="FAQ"
        accent="Questions"
        dek="Common questions about our hardware and software solutions."
        band="surface"
        className={FULL_BLEED}
      >
        <ServiceFaq items={hspFaqs} />
      </ServiceSection>

      <ConsultationCTA
        title="Ready to Equip Your Business?"
        description="Get a personalized quote for hardware, software, or a complete IT solution. Our team is ready to help you find the right products at the best prices."
        buttonText="Get Your Quote"
      />
    </ServicePageLayout>
  );
}
